import React, { useEffect, useState } from 'react';
import { t } from '@/i18n';
import type { StreamAskBlock, StreamBlock, StreamTagBlock, StreamTagKind } from '@/utils/responsesStream';

export interface MindAskPayload {
  answer?: string;
  selected?: string;
}

interface MindResponseBlocksProps {
  blocks: StreamBlock[];
  live: boolean;
  renderText: (text: string) => React.ReactNode;
  onAnswer?: (questionId: string, payload: MindAskPayload) => Promise<void>;
}

const TAG_LABEL: Record<StreamTagKind, string> = {
  thinking: 'mind.stream.thinking',
  tool: 'mind.stream.tool',
  mcp: 'mind.stream.mcp',
  shell: 'mind.stream.shell',
};

function tagTitle(block: StreamTagBlock): string {
  const fallback = t(TAG_LABEL[block.kind]);
  return block.title.trim() || fallback;
}

function schemaFields(schema: Record<string, unknown> | undefined): Array<{ name: string; type: string; title: string }> {
  if (!schema || !schema.properties || typeof schema.properties !== 'object' || Array.isArray(schema.properties)) {
    return [];
  }
  return Object.entries(schema.properties as Record<string, unknown>).map(([name, raw]) => {
    const field = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
    const type = typeof field.type === 'string' ? field.type : 'string';
    const title = typeof field.title === 'string' ? field.title : name;
    return { name, type, title };
  });
}

const TagRow: React.FC<{ block: StreamTagBlock }> = ({ block }) => {
  const [open, setOpen] = useState(block.status === 'running');
  const detail = [block.args, block.body, block.output].filter((part) => part && part.trim()).join('\n\n');

  useEffect(() => {
    if (block.status !== 'running') setOpen(false);
  }, [block.status]);

  return (
    <div className={`msg-stream-tag msg-stream-tag--${block.status}`}>
      <button
        type="button"
        className="msg-stream-tag__summary"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {block.status === 'running' ? <i className="msg-stream-tag__spin" /> : <i className="msg-stream-tag__dot" />}
        <span className="msg-stream-tag__title">{tagTitle(block)}</span>
        {block.status === 'failed' ? <span className="msg-stream-tag__state">{t('mind.stream.failed')}</span> : null}
      </button>
      {open && detail ? <pre className="msg-stream-tag__body">{detail}</pre> : null}
    </div>
  );
};

const AskCard: React.FC<{
  block: StreamAskBlock;
  live: boolean;
  onAnswer?: (questionId: string, payload: MindAskPayload) => Promise<void>;
}> = ({ block, live, onAnswer }) => {
  const [text, setText] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const inputs = schemaFields(block.schema);
  const canAnswer = live && block.answer == null && !submitting;

  const submit = (payload: MindAskPayload) => {
    if (!canAnswer || !onAnswer) return;
    if (!payload.answer?.trim() && !payload.selected?.trim()) return;
    setSubmitting(true);
    void onAnswer(block.questionId, payload).catch(() => {
      setSubmitting(false);
    });
  };

  if (block.answer != null) {
    return (
      <div className="msg-stream-ask msg-stream-ask--done">
        <div className="msg-stream-ask__q">{block.question}</div>
        <div className="msg-stream-ask__a">{block.answer}</div>
      </div>
    );
  }

  return (
    <form
      className="msg-stream-ask"
      onSubmit={(event) => {
        event.preventDefault();
        if (inputs.length) {
          submit({ answer: JSON.stringify(fields) });
          return;
        }
        submit({ answer: text });
      }}
    >
      <div className="msg-stream-ask__q">{block.question}</div>
      {block.options.length ? (
        <div className="msg-stream-ask__options">
          {block.options.map((option) => (
            <button
              key={option}
              type="button"
              className="msg-stream-ask__option"
              disabled={!canAnswer}
              onClick={() => submit({ selected: option })}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
      {inputs.length ? (
        <div className="msg-stream-ask__fields">
          {inputs.map((field) => (
            <label key={field.name} className="msg-stream-ask__field">
              <span>{field.title}</span>
              {field.type === 'boolean' ? (
                <input
                  type="checkbox"
                  disabled={!canAnswer}
                  checked={fields[field.name] === 'true'}
                  onChange={(event) => setFields((current) => ({
                    ...current,
                    [field.name]: event.target.checked ? 'true' : 'false',
                  }))}
                />
              ) : (
                <input
                  type={field.type === 'number' || field.type === 'integer' ? 'number' : 'text'}
                  disabled={!canAnswer}
                  value={fields[field.name] ?? ''}
                  onChange={(event) => setFields((current) => ({ ...current, [field.name]: event.target.value }))}
                />
              )}
            </label>
          ))}
          <button type="submit" className="msg-stream-ask__submit" disabled={!canAnswer}>
            {t('mind.ask.submit')}
          </button>
        </div>
      ) : null}
      {!inputs.length && !block.options.length ? (
        <div className="msg-stream-ask__free">
          <input
            value={text}
            disabled={!canAnswer}
            placeholder={t('mind.ask.placeholder')}
            onChange={(event) => setText(event.target.value)}
          />
          <button type="submit" className="msg-stream-ask__submit" disabled={!canAnswer || !text.trim()}>
            {t('mind.ask.submit')}
          </button>
        </div>
      ) : null}
    </form>
  );
};

const MindResponseBlocks: React.FC<MindResponseBlocksProps> = ({ blocks, live, renderText, onAnswer }) => (
  <div className="msg-stream-blocks">
    {blocks.map((block, index) => {
      if (block.type === 'text') {
        if (!block.text) return null;
        const isLastText = live && index === blocks.length - 1;
        return (
          <div key={block.id} className="msg-stream-text">
            {renderText(block.text)}
            {isLastText ? <span className="msg-cursor" /> : null}
          </div>
        );
      }
      if (block.type === 'tag') return <TagRow key={block.id} block={block} />;
      return <AskCard key={block.id} block={block} live={live} onAnswer={onAnswer} />;
    })}
  </div>
);

export default MindResponseBlocks;
